import type { ReactNode } from 'react';

import { Card, cn, focusRing, Icon, MonoLabel, Skeleton } from '@/design-system';
import type { IntegrationAddresses } from '@/entities/integration';

import {
    claudeCodeCommand,
    claudeDesktopConfig,
    cursorConfig,
    type ConnectTarget,
} from '../connect-config';
import { integrationsCopy as copy } from '../copy';
import type { ApiKeyOwner } from '../hooks';
import { CopyButton } from './CopyButton';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';
const SUBTITLE =
    'm-0 font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold text-[var(--text-primary)]';

export interface ConnectGuideCardProps {
    owner: ApiKeyOwner;
    addresses: IntegrationAddresses | undefined;
    /** De net gemaakte sleutel: dan staat hij al in de voorbeelden. */
    secret: string | null;
}

/**
 * Hoe je je AI-assistent koppelt: het MCP-adres en per assistent de configuratie om
 * te plakken.
 *
 * De stappen per assistent zijn standaard ingeklapt (een native `<details>`): wie
 * niets koppelt, ziet alleen de korte uitleg, en wie wel koppelt, klapt alleen zijn
 * eigen assistent open. De voorbeelden zijn echt: het adres van dit portaal en, als
 * je net een sleutel maakte, die sleutel als Bearer-header. Anders staat er een
 * plaatshouder die opvalt. Voor het eigen bedrijf staat de REST API eronder, ook
 * ingeklapt, met de documentatie; de beheer-MCP heeft geen REST-kant.
 */
export function ConnectGuideCard({ owner, addresses, secret }: ConnectGuideCardProps) {
    const titleId = `connect-guide-${owner}`;

    if (!addresses) {
        return (
            <Card padding="lg" aria-busy="true">
                <Skeleton width="45%" height={15} label={copy.guideTitle} />
                <Skeleton height={14} className="mt-3" />
            </Card>
        );
    }

    const target: ConnectTarget = {
        serverName: owner === 'platform' ? 'smartkeur-beheer' : 'smartkeur',
        mcpUrl: addresses.mcpUrl,
        key: secret,
    };

    return (
        <Card padding="lg" as="section" aria-labelledby={titleId}>
            <h2
                id={titleId}
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {copy.guideTitle}
            </h2>
            <p className={`m-0 mt-1 ${SMALL}`}>
                {owner === 'platform' ? copy.platformGuideIntro : copy.guideIntro}
            </p>

            <MonoLabel size={10} tone="muted" className="mt-[12px] block">
                {copy.mcpAddress}
            </MonoLabel>
            <div className="mt-[4px] flex flex-wrap items-center gap-2">
                <code className="font-[family-name:var(--font-mono)] text-[13px] break-all text-[var(--text-primary)]">
                    {addresses.mcpUrl}
                </code>
                <CopyButton text={addresses.mcpUrl} />
            </div>
            <p className={`m-0 mt-[6px] ${SMALL}`}>{copy.keyPlaceholderNote}</p>

            <Snippet
                title={copy.claudeDesktop}
                hint={copy.claudeDesktopHint}
                code={claudeDesktopConfig(target)}
            />
            <Snippet
                title={copy.claudeCode}
                hint={copy.claudeCodeHint}
                code={claudeCodeCommand(target)}
            />
            <Snippet title={copy.cursor} hint={copy.cursorHint} code={cursorConfig(target)} />

            {owner === 'company' ? (
                <>
                    <Snippet
                        title={copy.chatgpt}
                        hint={copy.chatgptHint}
                        code={addresses.openApiUrl}
                    />
                    <Collapsible title={copy.restTitle}>
                        <p className={`m-0 mt-1 ${SMALL}`}>{copy.restIntro}</p>
                        <p className="m-0 mt-[8px] flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                            <a
                                href={addresses.docsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[var(--accent)] hover:underline"
                            >
                                {copy.openDocs}
                            </a>
                            <a
                                href={addresses.openApiUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[var(--accent)] hover:underline"
                            >
                                {copy.openApi}
                            </a>
                        </p>
                    </Collapsible>
                </>
            ) : null}
        </Card>
    );
}

function Snippet({ title, hint, code }: { title: string; hint: string; code: string }) {
    return (
        <Collapsible title={title}>
            <p className={`m-0 mt-1 ${SMALL}`}>{hint}</p>
            <div className="mt-[8px]">
                <CopyButton text={code} />
            </div>
            <pre className="m-0 mt-[8px] overflow-x-auto rounded-[7px] border border-[var(--border-subtle)] bg-[var(--surface-subtle)] p-[10px] font-[family-name:var(--font-mono)] text-[12px] leading-[1.5] whitespace-pre text-[var(--text-primary)]">
                {code}
            </pre>
        </Collapsible>
    );
}

/** Eén ingeklapt blok met de naam als kop: klik op de naam om het open te klappen. */
function Collapsible({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section
            aria-label={title}
            className="mt-[12px] border-t border-[var(--border-subtle)] pt-[10px]"
        >
            <details className="group">
                <summary
                    className={cn(
                        'flex min-h-[32px] cursor-pointer list-none items-center gap-2 rounded-[4px] [&::-webkit-details-marker]:hidden',
                        focusRing,
                    )}
                >
                    <Icon
                        name="chevronDown"
                        size="sm"
                        tone="muted"
                        className="-rotate-90 group-open:rotate-0"
                    />
                    <h3 className={SUBTITLE}>{title}</h3>
                </summary>
                <div className="pb-[2px] pl-6">{children}</div>
            </details>
        </section>
    );
}
